// @flow
import React, { useCallback, useState, useEffect } from 'react';
import { withStyles, withTheme } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import PICTO_VIDEO from '../../../assets/images/pictoVideo.svg';
import QUICK_SETUP_AVA from '../../../assets/images/quickSetupAva.png';
import ICON_COMPUTER_AUDIO from '../../../assets/icons/quickSetupComputerAudio.svg';
import ICON_MICROPHONE_PERMISSION from '../../../assets/icons/quickSetupMicrophone.svg';
import LocalStorage, { STORAGE_KEYS } from '../../services/localStorage';
import { trackMicPermission, track } from '../../utils/segment';
import { getPrimaryDisplay } from '../../utils/electron';
import { getOS } from '../../utils';

let micCheckInterval;

type Props = {
  t: Function,
  classes: Object,
  setAvailableMics: Function,
  setMicSelected: Function,
  createNewConversation: Function,
  setMacAudioSetup: Function,
  setUrlToOpenAfterStartingConvo: Function,
  microphoneAccess: string,
  needInternalAudioAccess: Boolean,
  setLangSelected: Function,
  theme: Object,
};

const InstallMacInternalAudio = ({
  t,
  classes,
  setAvailableMics,
  setMicSelected,
  createNewConversation,
  setMacAudioSetup,
  microphoneAccess,
  needInternalAudioAccess,
  setLangSelected,
  setUrlToOpenAfterStartingConvo,
  theme,
}: Props) => {
  const [step, setStep] = useState(needInternalAudioAccess || microphoneAccess !== 'granted' ? 1 : 4);
  const [micStatus, setMicStatus] = useState(microphoneAccess);
  const [driverInstalling, setDriverInstalling] = useState(false);
  const [driverInstalled, setDriverInstalled] = useState(false);
  const [error, setError] = useState(false);
  const { i18n } = useTranslation();
  const isDarkMode = theme.palette.type === 'dark';
  const INSTALL_MICROPHONE = isDarkMode ? '/setupAudio/installMicrophoneDark.gif' : '/setupAudio/installMicrophone.gif';
  const INSTALL_INTERNAL_AUDIO = isDarkMode
    ? '/setupAudio/installInternalAudioDark.gif'
    : '/setupAudio/installInternalAudio.gif';
  const OPEN_SYSTEM_PREFS = isDarkMode ? '/setupAudio/openSystemPrefsDark.gif' : '/setupAudio/openSystemPrefs.gif';

  useEffect(() => {
    // When we display this component,
    // we resize the window
    const currentWindow = window.electronCurrentWindow;
    if (!currentWindow) return;
    currentWindow.setBounds({
      x: 50,
      y: 50,
      width: 790,
      height: 670,
    });
  }, []);

  useEffect(() => {
    if (step === 4 && window.electronIPC) {
      window.electronIPC.sendGoInFloatingMode();
    }
  }, [step]);

  const setupMacMicrophone = useCallback(() => {
    window.electronIPC.onceMacMicrophoneAccessResult((event, macMicPermission) => {
      if (macMicPermission) {
        setMicStatus('granted');
        trackMicPermission('granted');
      } else {
        setMicStatus('denied');
        trackMicPermission('denied');
      }
    });
    window.electronIPC.sendAskMacMicrophoneAccess();
  }, [setMicStatus]);

  const checkMicPermissionChange = () => {
    const audioSetup = window.electronIPC.sendSyncCheckAudioSetup();
    if (audioSetup.micAccess === 'granted' && micStatus !== audioSetup.micAccess) {
      setMicStatus('granted');
      trackMicPermission('granted');
      clearInterval(micCheckInterval);
      micCheckInterval = null;
    }
  };

  const openMicSystemPreferences = () => {
    if (window.electronShell) {
      const micPreferencesUrl =
        getOS() === 'Mac'
          ? 'x-apple.systempreferences:com.apple.preference.security?Privacy_Microphone'
          : 'ms-settings:privacy-microphone';
      window.electronShell.openExternal(micPreferencesUrl);
      if (getOS() !== 'Mac') {
        // On windows, no need to reboot app, so
        // let's check permissions regularly
        micCheckInterval = setInterval(() => {
          checkMicPermissionChange();
        }, 500);
      }
    }
  };

  const installInternalAudio = useCallback(() => {
    if (window.electronIPC) {
      setDriverInstalling(true);
      window.electronIPC.onceMacInternalAudioInstallation((event, installResult) => {
        Sentry.captureException(
          '\n\nMAC INTERNAL AUDIO INSTALLATION RESULT:\n\n',
          JSON.stringify(installResult),
          '\n\n'
        );
        setDriverInstalling(false);
        if (installResult.isInstalled) {
          track('Audio Driver Downloaded');
          setDriverInstalled(true);
        } else {
          setError(true);
        }
      });
      window.electronIPC.sendSetupMacInternalAudio();
    }
  }, [setDriverInstalling]);

  const resizeWindowToDefault = () => {
    const { width, height } = getPrimaryDisplay().workAreaSize;
    const currentWindow = window.electronCurrentWindow;

    currentWindow.setBounds({
      x: 50,
      y: 50,
      width: Math.min(1160, width - 100),
      height: Math.min(900, height - 100),
    });
  };

  const tryAva = launchVideo => {
    const currentAvailableMics = [];
    const interfaceLang = i18n.language;

    resizeWindowToDefault();

    if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
      navigator.mediaDevices.enumerateDevices().then(devices => {
        devices
          .filter(d => d.kind === 'audioinput' && d.deviceId !== 'default')
          .forEach(device => {
            currentAvailableMics.push({
              value: device.deviceId,
              label: device.label,
            });
          });
        const avaInternalMic = currentAvailableMics.find(m => m.label && m.label.includes('Ava Computer Audio'));

        setAvailableMics(currentAvailableMics);
        setMicSelected(avaInternalMic ? avaInternalMic.value : '');
        if (avaInternalMic && avaInternalMic.value) {
          LocalStorage.set(STORAGE_KEYS.SELECTED_MIC_ID, avaInternalMic.value);
        }
        const speechLang = interfaceLang === 'fr' ? 'fr-FR' : 'en-US';
        localStorage.setItem('speechLang', speechLang);
        setLangSelected(speechLang);
        if (launchVideo) {
          setUrlToOpenAfterStartingConvo(
            interfaceLang === 'fr' ? 'https://www.ava.me/fr/cc-video' : 'https://www.ava.me/cc-video'
          );
        }
        createNewConversation();
        setMacAudioSetup();
      });
    }
  };

  if (error) {
    Sentry.captureException(error);
  }

  if (step === 1) {
    return (
      <div className={classes.container}>
        <Typography align="center" className={classes.setup}>
          {t('macInternalAudio.quickSetup')}
        </Typography>
        <Typography align="center" className={classes.titleHome}>
          {t('macInternalAudio.permissionsTitle')}
        </Typography>
        <Typography align="center" className={classes.subtitle}>
          {needInternalAudioAccess && (
            <div className={classes.permission}>
              <img src={ICON_COMPUTER_AUDIO} alt="computer audio icon" className={classes.permissionImg} />
              <span className={classes.permissionName}>{t('macInternalAudio.internalAudio')}</span>
            </div>
          )}
          {micStatus !== 'granted' && (
            <div className={classes.permission}>
              <img src={ICON_MICROPHONE_PERMISSION} alt="mic permission icon" className={classes.permissionImg} />
              <span className={classes.permissionName}>{t('macInternalAudio.microphone')}</span>
            </div>
          )}
        </Typography>
        <Button className={classes.enable} onClick={() => setStep(needInternalAudioAccess ? 2 : 3)}>
          {t('macInternalAudio.continue')}
        </Button>
      </div>
    );
  }
  const MIC_IMAGE = micStatus === 'not-determined' ? INSTALL_MICROPHONE : OPEN_SYSTEM_PREFS;
  let micImgClass = '';
  if (micStatus === 'granted') {
    micImgClass = classes.greenTick;
  } else if (micStatus === 'denied') {
    micImgClass = classes.systemPrefs;
  }

  if (step === 2) {
    return (
      <div className={classes.container}>
        <Typography align="center" className={classes.setup}>
          {t('macInternalAudio.quickSetup')}
        </Typography>
        <img
          src={driverInstalled ? '/setupAudio/avaSetupSuccess.svg' : INSTALL_INTERNAL_AUDIO}
          alt="enable internal audio"
          className={`${classes.image} ${driverInstalled ? classes.greenTick : ''}`}
        />
        <Typography align="center" className={classes.title}>
          {t(driverInstalled ? 'macInternalAudio.allowInternalAudioSuccess' : 'macInternalAudio.allowInternalAudio')}
        </Typography>

        {driverInstalling && (
          <Button className={classes.enable} disabled>
            {t('macInternalAudio.installingDriver')}
          </Button>
        )}

        {!driverInstalling && (
          <Button
            className={classes.enable}
            onClick={() => {
              if (!driverInstalled) {
                installInternalAudio();
              } else if (micStatus !== 'granted') {
                setStep(3);
              } else {
                setStep(4);
              }
            }}
          >
            {t(driverInstalled ? 'macInternalAudio.continue' : 'macInternalAudio.installDriver')}
          </Button>
        )}
      </div>
    );
  }

  if (step === 3) {
    return (
      <div className={classes.container}>
        <Typography align="center" className={classes.setup}>
          {t('macInternalAudio.quickSetup')}
        </Typography>
        <img
          src={micStatus === 'granted' ? '/setupAudio/avaSetupSuccess.svg' : MIC_IMAGE}
          alt="enable microphone"
          className={`${classes.image} ${micImgClass}`}
        />
        <Typography align="center" className={classes.title}>
          {micStatus === 'granted' && t('macInternalAudio.allowMicrophoneSuccess')}
          {micStatus === 'not-determined' && t('macInternalAudio.allowMicrophone')}
          {micStatus === 'denied' && t('macInternalAudio.allowMicrophoneInSystemPrefs')}
        </Typography>

        <Button
          className={classes.enable}
          onClick={() => {
            if (micStatus === 'not-determined') {
              setupMacMicrophone();
            } else if (micStatus === 'denied') {
              openMicSystemPreferences();
            } else {
              setStep(4);
            }
          }}
        >
          {micStatus === 'not-determined' && t('macInternalAudio.enablePermissions')}
          {micStatus === 'denied' && t('macInternalAudio.openSystemPrefs')}
          {micStatus === 'granted' && t('macInternalAudio.continue')}
        </Button>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <Typography align="center" className={classes.setup}>
        {t('macInternalAudio.quickSetup')}
      </Typography>
      <Typography align="center" className={classes.titleHome}>
        {t('macInternalAudio.done')}
      </Typography>
      <Typography align="center" className={classes.doneSubtitle}>
        {t('macInternalAudio.tryItNow')}
      </Typography>
      <div style={{ textAlign: 'center' }}>
        <img src={QUICK_SETUP_AVA} style={{ width: '100%', maxWidth: 200 }} alt="picto mic" />
      </div>

      <Button className={`${classes.enable} ${getOS() === 'Mac' ? classes.drag : ''}`} onClick={() => tryAva(true)}>
        <img src={PICTO_VIDEO} className={classes.pictoVideo} alt="picto video" /> {t('macInternalAudio.startAva')}
      </Button>
      <Typography align="center" className={classes.tryLater} onClick={() => tryAva(false)}>
        {t('macInternalAudio.tryLater')}
      </Typography>
    </div>
  );
};

const styles = theme => ({
  container: {
    paddingTop: 70,
    maxWidth: 800,
    margin: 'auto',
    paddingBottom: 30,
    paddingRight: 80,
    paddingLeft: 80,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  titleHome: {
    color: theme.palette.ava.blue,
    fontSize: 34,
    marginTop: theme.spacing(1),
    fontWeight: 800,
    position: 'relative',
    zIndex: 2,
  },
  title: {
    color: theme.palette.contrast,
    fontSize: 28,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(4),
    position: 'relative',
    zIndex: 2,
  },
  setup: {
    color: '#778290',
    fontSize: 26,
    fontWeight: 'bolder',
    marginTop: theme.spacing(1),
    position: 'relative',
    zIndex: 2,
  },
  subtitle: {
    color: 'white',
    fontWeight: 500,
    fontSize: 26,
    marginLeft: 'auto',
    marginRight: 'auto',
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2),
    position: 'relative',
    zIndex: 2,
    maxWidth: 500,
  },
  enable: {
    textTransform: 'none',
    color: 'white',
    borderRadius: 33,
    backgroundColor: theme.palette.ava.blue,
    fontSize: 26,
    fontWeight: 800,
    borderLeftStyle: 'solid !important',
    borderColor: 'lightgray !important',
    marginBottom: 12,
    '&:hover': {
      backgroundColor: theme.palette.ava.blue,
      color: 'white',
    },
    padding: '8px 30px',
    whiteSpace: 'nowrap',
    margin: 'auto',
    display: 'flex',
    marginTop: theme.spacing(5),
    minWidth: 320,
  },
  image: {
    margin: 'auto',
    display: 'block',
    maxHeight: 400,
    maxWidth: '100%',
    marginTop: -theme.spacing(5),
    marginBottom: -theme.spacing(5),
    zIndex: 0,
    position: 'relative',
  },
  greenTick: {
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(5),
    height: 100,
  },
  systemPrefs: {
    maxHeight: 800,
    marginTop: -theme.spacing(2),
    marginBottom: -theme.spacing(2),
  },
  pictoVideo: {
    marginRight: theme.spacing(2),
  },
  doneSubtitle: {
    color: '#778290',
    fontSize: 24,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  confirmationMessage: {
    background: 'linear-gradient(90deg, #263252 7.32%, #272728 82.56%)',
    borderRadius: 24,
    padding: '14px 20px',
    color: 'white',
    fontSize: 25,
  },
  pictoMic: {
    width: 50,
    height: 50,
    marginRight: theme.spacing(3),
  },
  drag: {
    WebkitAppRegion: 'drag',
  },
  tryLater: {
    color: theme.palette.ava.blue,
    fontSize: 20,
    marginTop: theme.spacing(2),
    fontWeight: 800,
    position: 'relative',
    cursor: 'pointer',
  },
  permission: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  permissionName: {
    display: 'inline-block',
    backgroundColor: '#2F3B59',
    opacity: 0.95,
    padding: 7,
    borderRadius: 40,
    height: 45,
    fontSize: 18,
    width: 230,
    marginLeft: 15,
  },
  permissionImg: {
    height: 75,
  },
});

export default withStyles(styles)(withTheme(InstallMacInternalAudio));
